// src/components/Matches.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Container, Button, Collapse } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BACKEND_BASE } from "./constant";

const Matches = () => {
  const [matchesData, setMatchesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "updatedAt",
    direction: "desc",
  });
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMatchesData = async () => {
      try {
        const response = await axios.get(`${BACKEND_BASE}/getMatches`);
        setMatchesData(response.data);
      } catch (error) {
        console.error("Error fetching matches:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMatchesData();
  }, []);

  const sortedData = () => {
    const sortableData = [...matchesData];
    if (sortConfig) {
      sortableData.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === "updatedAt") {
          aVal = new Date(a.updatedAt);
          bVal = new Date(b.updatedAt);
        } else if (sortConfig.key === "email") {
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
        } else if (sortConfig.key === "results") {
          aVal = a.results.length;
          bVal = b.results.length;
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getClassNamesFor = (name) =>
    sortConfig.key === name
      ? sortConfig.direction === "asc"
        ? "asc"
        : "desc"
      : "";

  // CSV export
  const convertToCSV = (data) => {
    const headers = [
      "Name",
      "Email",
      "Matched Email",
      "Score",
      "Explanation",
      "Last Updated",
    ];
    const csvRows = [headers.join(",")];

    data.forEach((user) => {
      const updatedAt = new Date(user.updatedAt).toLocaleString();
      user.results.forEach((match) => {
        const row = [
          `"${user.firstName || ""} ${user.lastName || ""}"`,
          `"${user.email}"`,
          `"${match.matchedEmail}"`,
          `"${match.firstName || ""} ${match.lastName || ""}"`,
          `"${(match.score * 100).toFixed(2)}%"`,
          `"${match.explanation || "—"}"`,
          `"${updatedAt}"`,
        ];
        csvRows.push(row.join(","));
      });
    });

    return csvRows.join("\n");
  };

  const downloadCSV = () => {
    const csv = convertToCSV(matchesData);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "matches_analytics.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <h1 className="my-4">Matches Analytics</h1>
      <div className="mb-3">
        <Button
          onClick={() => navigate("/")}
          className="mx-2"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
        >
          Back
        </Button>
        <Button
          onClick={downloadCSV}
          className="mx-2"
          style={{ backgroundColor: "rgb(72, 39, 110)" }}
          disabled={!matchesData.length}
        >
          Export to CSV
        </Button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : matchesData.length === 0 ? (
        <p>No matches found.</p>
      ) : (
        <Table responsive="sm" striped bordered hover>
          <thead>
            <tr>
              <th
                onClick={() => requestSort("email")}
                className={getClassNamesFor("email")}
              >
                User
              </th>
              <th
                onClick={() => requestSort("results")}
                className={getClassNamesFor("results")}
              >
                Number of Matches
              </th>
              <th
                onClick={() => requestSort("updatedAt")}
                className={getClassNamesFor("updatedAt")}
              >
                Last Updated
              </th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {sortedData().map((user, index) => (
              <React.Fragment key={user.email}>
                <tr>
                  <td>{`${user.firstName || ""} ${user.lastName || ""} (${user.email})`}</td>
                  <td>{user.results.length}</td>
                  <td>{new Date(user.updatedAt).toLocaleString()}</td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() =>
                        setOpenIndex(openIndex === index ? null : index)
                      }
                      variant="secondary"
                    >
                      {openIndex === index ? "Hide" : "Show"} Matches
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 0, border: "none" }}>
                    <Collapse in={openIndex === index}>
                      <div style={{ padding: "10px", background: "#f8f9fa" }}>
                        {user.results.length === 0 ? (
                          <p>No matches yet.</p>
                        ) : (
                          <Table size="sm" striped bordered>
                            <thead>
                              <tr>
                                <th>Matched Email</th>
                                <th>Score</th>
                                <th>Explanation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {user.results.map((match, idx) => (
                                <tr key={idx}>
                                  <td>{match.matchedEmail}</td>
                                  <td>{(match.score * 100).toFixed(2)}%</td>
                                  <td>{match.explanation || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default Matches;
